import { Coordinates } from '../../../../types';
import { nominatimService } from '../../../../services/geocoding/nominatimService';
import { extractRoadReference } from '../../../../utils/roadUtils';

interface AddressComponents {
  road?: string;
  house_number?: string;
  postcode?: string;
  city?: string;
  town?: string;
  village?: string;
  suburb?: string;
  neighbourhood?: string;
  county?: string;
  state?: string;
}

export const reverseGeocodingService = {
  /**
   * Get reverse geocoded address for coordinates
   */
  getAddress: async (coordinates: Coordinates): Promise<string | null> => {
    try {
      const reverseResult = await nominatimService.reverseGeocode({
        coordinates,
        zoom: 18, // High detail for street-level
      });

      if (!reverseResult.success || reverseResult.data.features.length === 0) {
        return null;
      }

      const feature = reverseResult.data.features[0];
      if (!feature?.properties?.address) {
        return null;
      }

      const address = feature.properties.address as AddressComponents;
      return reverseGeocodingService.buildAddressString(address);
    } catch {
      return null;
    }
  },

  /**
   * Extract road reference from road field (e.g., "A2" from "A2, Autostrada Wolności")
   * Also handles plain numbers like "305" which should be mapped to "DW305"
   */
  extractRoadReference,

  /**
   * Build a comprehensive address string from address components
   * Prioritizes road references for major roads while preserving full context
   */
  buildAddressString: (address: AddressComponents): string => {
    if (!address.road) {
      // Fallback to county/state if no road available
      const fallbackParts = [];
      if (address.county) fallbackParts.push(address.county);
      if (address.state) fallbackParts.push(address.state);
      return fallbackParts.join(', ') || '';
    }

    const addressParts = [];

    // Try to extract road reference and use it if available
    const roadReference = reverseGeocodingService.extractRoadReference(address.road);
    let roadDisplay = address.road;

    // If we found a road reference (like DW305 from "305"), use it instead
    if (roadReference) {
      roadDisplay = roadReference;
    }

    // For major roads, prioritize the road reference or full road field
    if (address.house_number) {
      addressParts.push(`${roadDisplay} ${address.house_number}`);
    } else {
      addressParts.push(roadDisplay);
    }

    // Add postal code and city/town
    if (address.postcode && (address.city || address.town || address.village)) {
      const locality = address.city || address.town || address.village;
      addressParts.push(`${address.postcode} ${locality}`);
    } else if (address.city || address.town || address.village) {
      // City without postal code
      const locality = address.city || address.town || address.village;
      addressParts.push(locality);
    }

    // Add suburb/neighbourhood if different from city
    const locality = address.city || address.town || address.village;
    const subArea = address.suburb || address.neighbourhood;
    if (subArea && subArea !== locality && !addressParts.some(part => part?.includes(subArea))) {
      addressParts.push(subArea);
    }

    return addressParts.join(', ');
  },

  /**
   * Extract road information from reverse geocoding result
   */
  extractRoadInfo: (address: AddressComponents): { road: string; hasHouseNumber: boolean } | null => {
    if (!address.road) {
      return null;
    }

    return {
      road: address.road,
      hasHouseNumber: !!address.house_number,
    };
  },

  /**
   * Check if address has sufficient detail for a useful suggestion
   */
  isValidAddress: (address: AddressComponents): boolean => {
    // Must have at least a road, or a city/town/village
    return !!(address.road || address.city || address.town || address.village);
  },
};